import { usePathname, useSearch as useSearchParams, RouterLink as Link } from "./router";
import { useMemo, type ReactNode } from 'react'
import { cn } from './utils'

export interface NavTab {
  href: string
  label: string
  /** Optional pre-rendered icon JSX. Server components must instantiate the icon
   * here so the lucide component reference doesn't cross the server→client
   * boundary. Omit for a text-only tab. */
  icon?: ReactNode
  /** Override the path used for active matching (defaults to href). Useful when
   * the tab's link target differs from the path it should highlight on. */
  activeHref?: string
  /** Optional count badge trailing the label — the pill sub-nav's per-tab count
   * (retires the prototype PillNav's `count`). */
  count?: number
}

/** Pick the most specific tab target for the current location. A target may
 *  carry a query string (e.g. `/issues?view=board`); such a target matches only
 *  when its pathname matches exactly AND every one of its query params equals
 *  the current ones, so sibling view tabs that differ only by `?view=` light up
 *  independently. Plain (query-less) targets still match by pathname prefix, so
 *  detail routes like `/lens/projects/foo` keep the "Projects" tab
 *  (target `/lens/projects`) active. When several targets match, the one with
 *  the most matched query params wins, then the longest pathname — so a
 *  `?view=board` tab beats the bare list tab, and `/lens/projects` beats `/lens`.
 *
 *  This is the single source of tab active-state matching, shared by `NavTabs`
 *  (the tab-row primitive) and `PageNav` (which also matches `inlineTabs` and
 *  its `titleHref` against the same rules). To keep arbitration single even when
 *  the target sets differ, `PageNav` computes the winner over its *combined* set
 *  and hands it down via `NavTabs`' `activeTarget` prop, so the bottom tab row
 *  defers to the same decision rather than re-running this over its tabs alone. */
export function pickActiveTarget(
  pathname: string,
  search: URLSearchParams,
  targets: string[],
): string | null {
  let best: string | null = null
  let bestScore = -1
  for (const t of targets) {
    const qIdx = t.indexOf('?')
    const tPath = qIdx === -1 ? t : t.slice(0, qIdx)
    const tQuery = qIdx === -1 ? '' : t.slice(qIdx + 1)
    const pathExact = pathname === tPath
    const pathOk = pathExact || pathname.startsWith(tPath === '/' ? '/' : tPath + '/')
    if (!pathOk) continue
    let queryParams = 0
    if (tQuery) {
      // Query-bearing tabs match on the exact pathname only.
      if (!pathExact) continue
      let queryOk = true
      for (const [k, v] of new URLSearchParams(tQuery)) {
        queryParams++
        if (search.get(k) !== v) { queryOk = false; break }
      }
      if (!queryOk) continue
    }
    const score = queryParams * 100000 + tPath.length
    if (score > bestScore) { bestScore = score; best = t }
  }
  return best
}

export interface NavTabsProps {
  tabs: NavTab[]
  /** Extra classes merged onto the tab-row container (the default is a left
   *  aligned `flex items-center gap-1`). */
  className?: string
  /** Pre-arbitrated active target, computed by a parent against a *wider* set of
   *  targets (e.g. `PageNav`, which also weighs `inlineTabs` + `titleHref`). When
   *  provided, NavTabs skips its own `pickActiveTarget` and lights the tab whose
   *  target equals this value — so a global winner outside this tab set (a
   *  deeper `titleHref`) correctly leaves every bottom tab dark. Omit it and
   *  NavTabs self-computes over just its own `tabs` (the standalone case). */
  activeTarget?: string | null
  /** Controlled (local-state) mode: the selected tab's identity, matched against
   * each tab's `activeHref ?? href`. Pass WITH `onSelect` to drive the strip from
   * React state — it then renders `<button>`s and performs no navigation. Omit
   * both for the default URL-driven mode (unchanged: `<Link>`s + route matching).
   * This is what lets a local-state surface (the /styleguide prototype) share the
   * one pill strip instead of forking a look. */
  value?: string
  /** Controlled-mode selection handler. Receives the tab's `activeHref ?? href`
   * identity. Presence of this prop switches the strip to button rendering — and,
   * critically, to a render path that calls NO route hooks, so a Suspense-free
   * island (the /styleguide prototypes) can use it without tripping Next's
   * static-render Suspense guard. */
  onSelect?: (key: string) => void
}

/** The presentational pill row — pure, hook-free. Renders `<button>`s when an
 *  `onSelect` handler is present (controlled/local-state), `<Link>`s otherwise
 *  (URL nav). Both variants share the exact same pill markup + active styling, so
 *  the look is identical whichever mode drives it. */
function NavTabsRow({
  tabs,
  activeTarget,
  onSelect,
  className,
}: {
  tabs: NavTab[]
  activeTarget: string | null
  onSelect?: (key: string) => void
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {tabs.map((t) => {
        const target = t.activeHref ?? t.href
        const active = target === activeTarget
        const tabClass = cn(
          // Fully-rounded pill tabs: the row carries no border and inactive tabs no
          // fill — only the ACTIVE tab is filled, and with a LIGHT translucent wash
          // (`bg-foreground/15`, the workflow-detail prototype's value — NOT the
          // heavier `bg-muted`), so the current view reads without a dark blob (Matt,
          // 2026-07-23: "the selected item colour changed — back to lighter"). h-7
          // keeps the tab a standard 28px control.
          'inline-flex h-7 items-center gap-1.5 rounded-full px-3 type-body tracking-tight transition-colors whitespace-nowrap',
          active
            ? 'bg-foreground/15 font-medium text-foreground'
            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
        )
        // No icons in the tab strip — EVER (Matt, 2026-07-23: "we don't want icons
        // in that nav ever at all; we can have indicators, numbers, but not icons").
        // `NavTab.icon` is intentionally ignored here; only the count badge renders.
        const inner = (
          <>
            {t.label}
            {t.count != null ? (
              <span
                className={cn(
                  'rounded-full px-1.5 type-subtext tabular-nums',
                  active ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground',
                )}
              >
                {t.count}
              </span>
            ) : null}
          </>
        )
        // button-ignore: segmented pill sub-nav tab (mirrors the <Link> variant), not
        // a filled action button.
        return onSelect ? (
          <button
            key={t.href}
            type="button"
            onClick={() => onSelect(target)}
            aria-current={active ? 'page' : undefined}
            className={tabClass}
          >
            {inner}
          </button>
        ) : (
          <Link
            key={t.href}
            href={t.href}
            aria-current={active ? 'page' : undefined}
            className={tabClass}
          >
            {inner}
          </Link>
        )
      })}
    </div>
  )
}

/** URL-driven mode: derives the active tab from the live route. Calls the route
 *  hooks, so it is only rendered when NOT controlled (see NavTabs below). */
function UrlNavTabs({ tabs, className, activeTarget: activeTargetProp, value }: NavTabsProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const navParams = useMemo(
    () => new URLSearchParams(searchParams?.toString() ?? ''),
    [searchParams],
  )
  const selfTarget = useMemo(
    () => pickActiveTarget(pathname, navParams, tabs.map((t) => t.activeHref ?? t.href)),
    [pathname, navParams, tabs],
  )
  // Precedence: an explicit `value` still wins (a URL-rendered strip driven by
  // external state), then a parent-arbitrated target (PageNav), then self-compute
  // over just these tabs (the standalone URL case).
  const activeTarget =
    value !== undefined
      ? value
      : activeTargetProp !== undefined
        ? activeTargetProp
        : selfTarget
  return <NavTabsRow tabs={tabs} activeTarget={activeTarget} className={className} />
}

/** The canonical horizontal tab strip — a row of pill links with query-aware
 *  active highlighting. It is the single owner of tab-row markup + active-state
 *  matching: `PageNav` renders its tabs through it, and any in-body sub-tab strip
 *  (a section page's Runs/Workflows/Goals `?view=` switch, the /styleguide
 *  prototypes' local-state nav) should use it directly instead of hand-rolling a
 *  `<Link>`/`<button>` row.
 *
 *  Two modes, split so controlled mode is HOOK-FREE: pass `onSelect` (with
 *  `value`) and it renders buttons off local state, calling no route hooks — safe
 *  inside a Suspense-free island. Omit `onSelect` and it derives the active tab
 *  from the live route via `usePathname`/`useSearchParams`. */
export function NavTabs(props: NavTabsProps) {
  if (props.onSelect) {
    return (
      <NavTabsRow
        tabs={props.tabs}
        activeTarget={props.value ?? null}
        onSelect={props.onSelect}
        className={props.className}
      />
    )
  }
  return <UrlNavTabs {...props} />
}
